const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const SCREENSHOT_ENDPOINT = `${API_BASE_URL}/interview/screenshot`;
const CONSENT_KEY = "proctoring_screen_consent_v1";

class ScreenTracker {
  private sessionId: string;
  private intervalMs: number;
  private _sendScreenshot: (payload: any) => Promise<void>;
  private _stream: MediaStream | null = null;
  private _track: MediaStreamTrack | null = null;
  private _timer: NodeJS.Timeout | null = null;
  private _video: HTMLVideoElement | null = null;
  private _canvas: HTMLCanvasElement;
  private _ctx: CanvasRenderingContext2D;
  
  // Face capture mode (from video element)
  private _faceCaptureTimer: NodeJS.Timeout | null = null;
  private _videoElement: HTMLVideoElement | null = null;
  private _isFaceCaptureActive: boolean = false;

  constructor(sessionId: string, options: any = {}) {
    this.sessionId = sessionId;
    this.intervalMs = options.intervalMs || 30000;
    this._sendScreenshot = options.sendScreenshot || this._defaultSendScreenshot;
    this._canvas = document.createElement("canvas");
    this._ctx = this._canvas.getContext("2d", { willReadFrequently: true })!;
    
    // Accept video element for face capture
    if (options.videoElement) {
      this._videoElement = options.videoElement;
    }
  }

  async ensureConsent(consentCallback?: () => Promise<boolean>): Promise<boolean> {
    // Always show custom consent dialog if callback provided (even if stored)
    // This ensures user is aware screen sharing is happening
    if (typeof consentCallback === "function") {
      const accepted = await consentCallback();
      if (accepted && window.localStorage) {
        window.localStorage.setItem(CONSENT_KEY, "true");
      }
      return accepted;
    }

    // If no callback, check stored consent
    const stored =
      typeof window !== "undefined" && window.localStorage
        ? window.localStorage.getItem(CONSENT_KEY)
        : null;
    
    if (stored === "true") {
      return true;
    }

    return false;
  }

  async start(consentCallback?: () => Promise<boolean>) {
    if (this._timer) {
      return;
    }

    const consentGranted = await this.ensureConsent(consentCallback);
    if (!consentGranted) {
      return;
    }

    try {
      // This will show the browser's native screen share picker
      this._stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: "monitor", // Prefer full screen
        } as any,
        audio: false, // We only need video for screenshots
      });
      
      this._track = this._stream.getVideoTracks()[0];
      if (!this._track) {
        throw new Error("Display media track not available");
      }
      
      // Listen for when user stops sharing
      this._track.addEventListener("ended", () => {
        this.stop();
      });
      
      this._timer = setInterval(() => this._captureFrame(), this.intervalMs);
    } catch (error: any) {
      // Screen tracking failed
      // Clear consent on error so user can try again
      if (window.localStorage) {
        window.localStorage.removeItem(CONSENT_KEY);
      }
    }
  }

  stop() {
    this.stopFaceCapture(); // Stop face capture if active
    
    if (this._timer) {
      clearInterval(this._timer);
      this._timer = null;
    }
    if (this._track) {
      this._track.stop();
      this._track = null;
    }
    if (this._stream) {
      this._stream.getTracks().forEach((track) => track.stop());
      this._stream = null;
    }
    if (this._video) {
      this._video.srcObject = null;
      this._video = null;
    }
  }

  /**
   * Capture a single face screenshot when face detection popup appears
   * Takes only one screenshot per popup appearance (not continuous)
   */
  startFaceCapture(videoElement?: HTMLVideoElement) {
    const video = videoElement || this._videoElement;
    if (!video) {
      return;
    }

    this._videoElement = video;

    // Capture only one screenshot immediately (no interval timer)
    this._captureFaceFrame();
  }

  /**
   * Stop capturing face screenshots (cleanup method)
   */
  stopFaceCapture() {
    // Clear any pending timers (if any)
    if (this._faceCaptureTimer) {
      clearInterval(this._faceCaptureTimer);
      this._faceCaptureTimer = null;
    }
    this._isFaceCaptureActive = false;
  }

  /**
   * Capture a frame from the video element (candidate's face)
   */
  private async _captureFaceFrame() {
    if (!this._videoElement || !this.sessionId) {
      return;
    }

    try {
      const video = this._videoElement;
      
      // Check if video is ready
      if (video.readyState !== HTMLMediaElement.HAVE_ENOUGH_DATA) {
        return;
      }

      // Get video dimensions
      const width = video.videoWidth || 640;
      const height = video.videoHeight || 480;

      if (width === 0 || height === 0) {
        return;
      }


      // Draw video frame to canvas
      this._canvas.width = width;
      this._canvas.height = height;
      this._ctx.drawImage(video, 0, 0, width, height);
      
      // Convert to base64 JPEG
      const image = this._canvas.toDataURL("image/jpeg", 0.8); // Higher quality for face capture
      
      await this._sendScreenshot({
        sessionId: this.sessionId,
        image,
        timestamp: new Date().toISOString(),
        metadata: {
          type: "face_capture",
          source: "video_element",
          width,
          height,
        },
      });
      
    } catch (error) {
    }
  }

  private async _captureFrame() {
    if (!this._track || !this.sessionId) {
      return;
    }

    try {
      // Use HTMLVideoElement to extract current frame instead of ImageCapture.grabFrame()
      if (!this._video) {
        this._video = document.createElement('video');
        this._video.srcObject = new MediaStream([this._track]);
        this._video.play();
      }
      this._canvas.width = this._video.videoWidth;
      this._canvas.height = this._video.videoHeight;
      this._ctx.drawImage(this._video, 0, 0, this._video.videoWidth, this._video.videoHeight);
      const image = this._canvas.toDataURL("image/jpeg", 0.6);
      await this._sendScreenshot({
        sessionId: this.sessionId,
        image,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      this.stop();
    }
  }

  private async _defaultSendScreenshot(payload: any) {
    try {
      await fetch(SCREENSHOT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      // Screenshot uploaded
    } catch (error) {
      // Screenshot upload failed
    }
  }
}

export function createScreenTracker(sessionId: string, options?: any) {
  return new ScreenTracker(sessionId, options);
}

export default ScreenTracker;

